import { FC } from 'react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { LuHistory } from 'react-icons/lu';
import { StatusHistory } from './StatusHistory';
import { ActivityLog } from './ActivityLog';
import { Order } from 'src/trpc';

interface ActivityDrawerProps {
  order: Order;
}

/**
 * Order Activity panel.
 *
 * Built on the Radix-backed `Sheet` used by every other right-side panel in the admin UI, not
 * on vaul's `Drawer`. The drag-to-dismiss drawer left focus on the obscured trigger behind the
 * overlay, so Tab walked into the Notes textarea underneath it. `Sheet` is a real modal dialog:
 * it places initial focus inside the panel, contains Tab, closes on Escape, restores focus to
 * the trigger, and marks the background inert.
 *
 * The panel also renders through a portal, which puts it outside `.order-detail-mobile-type`.
 * The page-scoped 44px control floor therefore does not reach it, so each control here carries
 * its own `min-h-11`.
 */
export const ActivityDrawer: FC<ActivityDrawerProps> = ({ order }) => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="flex items-center gap-2 text-text-light hover:text-primary"
        >
          <LuHistory className="w-4 h-4" aria-hidden="true" />
          Activity
        </Button>
      </SheetTrigger>
      {/* A fixed 500px panel inset 16px on each side overflows any phone, so below `sm` the
          panel takes the viewport minus those insets instead. `[&>button]` is the sheet's own
          corner close control, sized to the same 44px target as everything else in here. */}
      <SheetContent
        side="right"
        className="inset-y-auto right-4 left-auto top-4 bottom-4 h-auto w-[calc(100vw-2rem)] min-w-0 max-w-full gap-0 sm:w-[500px] sm:max-w-none rounded-lg border p-0 overflow-hidden [&>button]:min-h-11 [&>button]:min-w-11 [&>button]:flex [&>button]:items-center [&>button]:justify-center"
      >
        <div className="bg-white h-full w-full min-w-0 max-w-full grow p-5 flex flex-col rounded-lg">
          <SheetHeader className="px-0 pt-0 text-left">
            <SheetTitle className="text-lg font-semibold">Order Activity</SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              View the complete timeline of status changes and actions for this order
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 min-w-0 max-w-full overflow-y-auto">
            <Tabs defaultValue="status-history" className="w-full min-w-0 max-w-full">
              <TabsList className="grid w-full grid-cols-2 h-auto p-1">
                <TabsTrigger value="status-history" className="min-h-11 whitespace-normal">
                  Status History
                </TabsTrigger>
                <TabsTrigger value="activity-log" className="min-h-11 whitespace-normal">
                  Activity Log
                </TabsTrigger>
              </TabsList>

              <TabsContent value="status-history" className="mt-4 min-w-0 max-w-full">
                <StatusHistory statusHistory={order.statusHistory} />
              </TabsContent>

              <TabsContent value="activity-log" className="mt-4 min-w-0 max-w-full">
                <ActivityLog order={order} />
              </TabsContent>
            </Tabs>
          </div>

          <SheetFooter className="px-0 pb-0">
            <SheetClose asChild>
              <Button variant="outline" className="w-full h-auto min-h-11">
                Close
              </Button>
            </SheetClose>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
